import { Band, BandStack } from '@/components/ui/Band'
import { ButtonLink } from '@/components/ui/Button'
import { PageHero } from '@/components/ui/PageHero'
import { Eyebrow } from '@/components/ui/Section'
import { Stagger } from '@/components/motion/Reveal'
import { useDocumentMeta } from '@/lib/seo'
import { PARTNER_CONTACT_PATH, ROUTES } from '@/lib/site'

const TOTAL_LAYERS = 5

const ENFORCED = [
  {
    name: 'A credential that can write is refused',
    body: 'The connector layer verifies that a credential cannot write to your system, and refuses to save it if it can. Read-only is checked at the moment of storage rather than promised in documentation.',
    note: 'Connectors are not built yet. The rule is designed in before the first one ships, because retrofitting it later means auditing every integration.',
  },
  {
    name: 'The engine cannot creep in',
    body: 'One module touches the vendored engine, and every import in it is function-local. Starting the API does not load a machine-learning stack. This is enforced three ways, including an import test run with the vendored tree renamed away.',
    note: 'A narrow seam is a security property as much as an architectural one: it is the list of places a model can see your data.',
  },
  {
    name: 'The vendored engine is a mirror, not a fork',
    body: 'It is a read-only copy of the platform monorepo. An in-place edit fails a blocking drift gate in CI. Divergence is allowed only by moving the file out of the mirror and recording it.',
    note: 'Which means an unreviewed change cannot appear in the code that reads your figures.',
  },
  {
    name: 'One place for tokens and retries',
    body: 'Every read from the browser goes through a single request layer: the bearer token, a single-flight refresh, rate-limit handling and conditional requests are implemented once rather than per screen.',
    note: 'Duplicated auth logic is how a screen ends up quietly reading without a token.',
  },
  {
    name: 'Confirmed email, enforced our side',
    body: 'Sign-in runs through a hosted identity provider, and the API enforces email confirmation regardless of how the provider is configured. An unconfirmed account cannot read a figure.',
    note: 'A local password provider stays available so the product is never dependent on a single vendor being reachable.',
  },
  {
    name: 'Migrations own the schema',
    body: 'The database shape is defined by migrations in every deployment, and the first one applies and reverses cleanly. No environment carries a hand-made column.',
    note: 'Reversibility is the part that matters. A migration that cannot be undone is an outage waiting for a bad week.',
  },
]

const NOT_PROVEN = [
  {
    name: 'No completed restore drill',
    body: 'We hold one dated restore-rehearsal record, and it documents a pre-flight that did not execute — availability check not run, target recovery time pending. It is a record of a failed attempt, not evidence of disaster recovery, and we will not present it as one. A real drill has to be run.',
  },
  {
    name: 'Identity provider untested against a live project',
    body: "The integration is complete and tested against locally-minted tokens in the provider's claim shape. That proves our half of the contract and cannot prove theirs. Two hand-checks close it once a real project exists.",
  },
  {
    name: 'No production data yet, therefore no scale evidence',
    body: 'Parser behaviour on genuinely large or pathological workbooks is bounded by design intent, not by measurement, because we do not yet have the corpus. This is the main reason we are asking for real exports.',
  },
]

export default function Security() {
  useDocumentMeta(ROUTES.security)

  return (
    <BandStack>
      <Band layer={1} total={TOTAL_LAYERS} tone="ice" routeTab="security">
        <PageHero
          crumb="Data & security"
          title="You are handing over your general ledger. Here is exactly what happens to it."
          lede="Financial data is the most sensitive export a company has, and a security page full of adjectives is worth nothing. Below is what is enforced in code, what is a stated posture, and what we have not yet proven. The third list is not empty, and we would rather publish it than be asked about it later."
        />
      </Band>

      <Band layer={2} total={TOTAL_LAYERS} tone="warm" labelledBy="enforced-heading">
        <Eyebrow className="text-primary">Enforced in code</Eyebrow>
        <h2
          id="enforced-heading"
          className="mt-6 max-w-3xl text-balance text-[28px] font-extrabold leading-[1.13] tracking-[-0.025em] sm:text-[38px]"
        >
          Constraints that fail a build, not a checklist.
        </h2>
        <Stagger
          variant="up"
          step={100}
          as="ul"
          itemAs="li"
          className="mt-10 grid gap-4 lg:grid-cols-2"
        >
          {ENFORCED.map((item) => (
            <div
              key={item.name}
              className="card-spot rounded-card border border-line bg-white/85 backdrop-blur p-6 shadow-card sm:p-7"
            >
              <h3 className="text-[17px] font-extrabold leading-snug tracking-[-0.015em]">
                {item.name}
              </h3>
              <p className="mt-4 text-[14px] leading-[1.75] text-muted">{item.body}</p>
              <p className="mt-4 border-t border-line pt-4 text-[13px] leading-[1.7] text-muted-soft">
                {item.note}
              </p>
            </div>
          ))}
        </Stagger>
      </Band>

      <Band layer={3} total={TOTAL_LAYERS} tone="ice" labelledBy="residency-heading">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <Eyebrow className="text-primary">Where your data would live</Eyebrow>
            <h2
              id="residency-heading"
              className="mt-6 text-balance text-[27px] font-extrabold leading-[1.14] tracking-[-0.025em] sm:text-[34px]"
            >
              A separate instance, with its own database.
            </h2>
            <p className="mt-5 max-w-measure text-[15px] leading-[1.75] text-muted">
              Two reasons, both practical. Spreadsheet parsing is a spiky allocator with no upper
              bound on what an upload contains, so one unusual workbook must not be able to degrade
              anything else. And customer financials should not share a database whose ownership
              boundary is still being settled elsewhere.
            </p>
            <p className="mt-4 max-w-measure text-[15px] leading-[1.75] text-muted">
              India is the domicile and INR the primary currency, which is also the assumption the
              parser and the panel prefixes are built around.
            </p>
          </div>

          <div className="rounded-card border border-projected/30 bg-projected/[0.06] p-6 sm:p-8">
            <Eyebrow className="text-[#9a6212]">Decision pending</Eyebrow>
            <h3 className="mt-4 text-[19px] font-extrabold leading-snug tracking-[-0.015em]">
              The dedicated instance is not yet provisioned.
            </h3>
            <p className="mt-4 text-[14px] leading-[1.75] text-muted">
              It is a real recurring cost and it has not been approved yet, so it is stated here as
              the intended posture rather than as something already in place.
            </p>
            <p className="mt-4 text-[14px] leading-[1.75] text-muted">
              If you are evaluating this as a design partner, ask us where it stands on the day you
              ask. The answer will be specific.
            </p>
          </div>
        </div>
      </Band>

      <Band layer={4} total={TOTAL_LAYERS} tone="mint" labelledBy="unproven-heading">
        <Eyebrow className="text-primary">Not yet proven</Eyebrow>
        <h2
          id="unproven-heading"
          className="mt-6 max-w-3xl text-balance text-[28px] font-extrabold leading-[1.13] tracking-[-0.025em] sm:text-[38px]"
        >
          The list most security pages leave out.
        </h2>
        <p className="mt-5 max-w-measure text-[15px] leading-[1.75] text-muted">
          A vendor who tells you only what passed has told you nothing about their judgement. These
          are open, dated and tracked internally, and they will be closed before anything is called
          production.
        </p>
        <Stagger
          variant="up"
          step={90}
          as="ul"
          itemAs="li"
          className="mt-10 grid gap-4 lg:grid-cols-3"
        >
          {NOT_PROVEN.map((item) => (
            <div
              key={item.name}
              className="card-spot rounded-card border border-line bg-white/85 backdrop-blur p-6 shadow-card"
            >
              <h3 className="text-[16px] font-extrabold leading-snug tracking-[-0.015em]">
                {item.name}
              </h3>
              <p className="mt-3 text-[13.5px] leading-[1.7] text-muted">{item.body}</p>
            </div>
          ))}
        </Stagger>
        <p className="mt-8 max-w-measure text-[14.5px] leading-[1.75] text-muted">
          The repository also contains adversarial self-audits that contradict earlier marketing
          material. Where an audited figure and a marketing figure disagree, the audited one wins
          everywhere.
        </p>
      </Band>

      <Band layer={5} total={TOTAL_LAYERS} tone="ice" labelledBy="security-cta" showMark={false}>
        <div className="mx-auto max-w-3xl text-center">
          <h2
            id="security-cta"
            className="text-balance text-[26px] font-extrabold leading-[1.14] tracking-[-0.025em] sm:text-[34px]"
          >
            Send an anonymised export first.
          </h2>
          <p className="mx-auto mt-5 max-w-[56ch] text-pretty text-[15px] leading-[1.75] text-muted">
            Values stripped, shape intact. It is enough for us to tell you what would compute, and
            it does not require you to trust us with anything real yet.
          </p>
          <div className="mt-9 flex justify-center">
            <ButtonLink to={PARTNER_CONTACT_PATH} size="lg">
              Talk to us
            </ButtonLink>
          </div>
        </div>
      </Band>
    </BandStack>
  )
}
